import { bytesToMB } from '@eluve/utils';

/**
 * Browser storage diagnostics shared by the recording pre-flight check and the
 * OPFS write pipelines. Pure reads of `navigator.storage` — no logging — so it
 * runs in both the (Window) transcription machine and the (Worker) pipelines.
 */
export interface StorageEstimate {
  usageBytes: number;
  quotaBytes: number;
  /** Headroom against the per-origin quota: `quota - usage`, floored at 0. */
  availableBytes: number;
  /**
   * Whether the origin's data is exempt from eviction under storage pressure
   * (`navigator.storage.persisted()`). `null` when the API is unavailable.
   *
   * Load-bearing for telling the two failure ceilings apart: best-effort data
   * (`false`) can be silently evicted when the *device disk* fills, so a write
   * failure with `usage` well under `quota` points at low disk, not the
   * per-origin cap. Persisted data (`true`) is never auto-evicted, so a failure
   * there is the origin actually hitting its quota.
   */
  persisted: boolean | null;
}

const readPersisted = async (): Promise<boolean | null> => {
  if (typeof navigator.storage?.persisted !== 'function') {
    return null;
  }
  try {
    return await navigator.storage.persisted();
  } catch {
    return null;
  }
};

/**
 * Reads the origin's storage usage/quota/persistence. Returns `null` when the
 * browser doesn't support `navigator.storage.estimate()` or reports no quota,
 * so callers can skip rather than treat "unknown" as "full".
 */
export const getStorageEstimate = async (): Promise<StorageEstimate | null> => {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.storage?.estimate !== 'function'
  ) {
    return null;
  }

  const { usage, quota } = await navigator.storage.estimate();
  if (!quota) {
    return null;
  }

  const usageBytes = usage ?? 0;
  return {
    usageBytes,
    quotaBytes: quota,
    availableBytes: Math.max(0, quota - usageBytes),
    persisted: await readPersisted(),
  };
};

/**
 * Flatten an estimate into MB-scaled log fields. Shared so the pre-flight check
 * and the write-failure capture report the same shape to Datadog.
 */
export const toStorageLogFields = (estimate: StorageEstimate) => ({
  availableMb: bytesToMB(estimate.availableBytes),
  usageMb: bytesToMB(estimate.usageBytes),
  quotaMb: bytesToMB(estimate.quotaBytes),
  persisted: estimate.persisted,
});
